import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Button, Menu } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { SocialExportPublishPlatformMenuItem } from './SocialExportPublishPlatformMenuItem';
import { socialExportPublishMessages } from './social-export-publish-messages';

export interface SocialExportPublishPlatformDropdownProps {
  /** Platforms the user can open the upload wizard for. */
  platforms: SocialPlatform[];
  /** When true, the trigger stays disabled (e.g. no renderable media sources). */
  disabled: boolean;
  /** Restriction message per platform id; present when publishing is not allowed. */
  restrictionByPlatformId?: Record<string, string | undefined>;
  /** Called with the platform id when the user picks a menu entry. */
  onSelectPlatform: (platformId: string) => void;
}

/**
 * Presentational "Publish to" menu: one item per platform; parent handles navigation and data loading.
 */
export function SocialExportPublishPlatformDropdown({
  platforms,
  disabled,
  restrictionByPlatformId,
  onSelectPlatform,
}: SocialExportPublishPlatformDropdownProps) {
  const { $t } = useIntl();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePlatformSelect = (platformId: string) => {
    onSelectPlatform(platformId);
    handleClose();
  };

  if (platforms.length === 0) {
    return null;
  }

  return (
    <>
      <Button
        size="small"
        disabled={disabled}
        onClick={handleClick}
        endIcon={<ArrowDropDownIcon />}
      >
        {$t(socialExportPublishMessages.publishTo)}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {platforms.map((platform) => (
          <SocialExportPublishPlatformMenuItem
            key={platform.id}
            platform={platform}
            restrictionMessage={restrictionByPlatformId?.[platform.id]}
            onSelect={handlePlatformSelect}
          />
        ))}
      </Menu>
    </>
  );
}
